export class BlinkingHighlight {
  _active = false;
  _progress = 0; // 0 to 1, represents animation progress
  _direction = 1; // 1 for forward, -1 for backward
  _animationFrameId = null;
  _autoStopId = null;
  _armClickId = null;
  _detachDocClick = null;
  _colorOverride = null;
  _startTime = null;
  _cycleDuration = 800; // Full cycle duration in ms

  start({
          blinkMs = 1400, // Full cycle duration (fade in + fade out)
          totalMs = 5000,
          stopOnClick = true,
          clickDelayMs = 50,
          colorOverride = null
        } = {}) {
    if (this._active) return;
    this._active = true;
    this._colorOverride = colorOverride;
    this._cycleDuration = Math.max(100, blinkMs | 0);
    this._startTime = performance.now();

    // Animation loop using requestAnimationFrame for smooth transitions
    const animate = (currentTime) => {
      if (!this._active) return;

      const elapsed = currentTime - this._startTime;
      const cycleProgress = (elapsed % this._cycleDuration) / this._cycleDuration;

      // Use sine wave for smooth easing (0 -> 1 -> 0)
      this._progress = (Math.sin(cycleProgress * Math.PI * 2 - Math.PI / 2) + 1) / 2;

      this._animationFrameId = requestAnimationFrame(animate);
    };

    this._animationFrameId = requestAnimationFrame(animate);

    if (stopOnClick) {
      this._armClickId = setTimeout(() => {
        const handler = () => this.stop();
        document.addEventListener('click', handler, { once: true, passive: true });
        this._detachDocClick = () => document.removeEventListener('click', handler);
        this._armClickId = null;
      }, Math.max(0, clickDelayMs | 0));
    }

    if (Number.isFinite(totalMs) && totalMs > 0) {
      this._autoStopId = setTimeout(() => this.stop(), totalMs);
    }
  }

  stop() {
    if (!this._active) return;

    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
    }
    clearTimeout(this._autoStopId);
    if (this._armClickId) clearTimeout(this._armClickId);
    if (this._detachDocClick) this._detachDocClick();

    this._animationFrameId = this._autoStopId = this._armClickId = null;
    this._detachDocClick = null;
    this._active = false;
    this._progress = 0;
    this._colorOverride = null;
    this._startTime = null;
  }

  get isOn()     { return this._active; } // Now always true when active
  get isActive() { return this._active; }
  get override() { return this._colorOverride; }
  get progress() { return this._progress; } // New: 0-1 progress value
}

/**
 * Pick and interpolate between colors based on blink progress
 * @param {string|Function} onColor - Color when fully "on" (progress = 1)
 * @param {string|Function} offColor - Color when fully "off" (progress = 0)
 * @param {number} progress - Animation progress (0 to 1)
 * @param {string} override - Optional color override
 * @returns {string|null} Interpolated color
 */
export const pickPulsedColor = (onColor, offColor, progress, override) => {
  const evalMaybe = v => (typeof v === 'function' ? v() : v);

  const on = override != null ? override : evalMaybe(onColor);
  const off = offColor === undefined ? null : evalMaybe(offColor);

  if (off === null) return on;

  // Interpolate between off and on based on progress
  return interpolateColor(off, on, progress);
};

// Legacy support - kept for backward compatibility
export const pickColor = (on, off, isBlinkOn, override) => {
  const evalMaybe = v => (typeof v === 'function' ? v() : v);
  if (isBlinkOn) {
    return (override != null ? override : evalMaybe(on));
  }
  return off === undefined ? null : evalMaybe(off);
};

// Utility function to interpolate between two colors
const interpolateColor = (color1, color2, progress) => {
  // Parse colors (supports rgba, rgb, hex)
  const parseColor = (color) => {
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
      return match ? {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3]),
        a: match[4] ? parseFloat(match[4]) : 1
      } : null;
    }
    if (color.startsWith('rgb')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      return match ? {
        r: parseInt(match[1]),
        g: parseInt(match[2]),
        b: parseInt(match[3]),
        a: 1
      } : null;
    }
    if (color.startsWith('#')) {
      const hex = color.substring(1);
      const bigint = parseInt(hex, 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
        a: 1
      };
    }
    return null;
  };

  const c1 = parseColor(color1);
  const c2 = parseColor(color2);

  if (!c1 || !c2) return color1;

  const r = Math.round(c1.r + (c2.r - c1.r) * progress);
  const g = Math.round(c1.g + (c2.g - c1.g) * progress);
  const b = Math.round(c1.b + (c2.b - c1.b) * progress);
  const a = c1.a + (c2.a - c1.a) * progress;

  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
